import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'
import { AppServices } from 'middleware'
import { DateFormatter } from 'uiComponents/date'
import { ExtensibleReactTableComponentProps, ReactTable, TableColumn } from 'uiComponents/table/reactTable'
import { downloadFile } from 'utils/downloadFile'
import { DiscountPool } from 'discounts/types'
import { getDiscountUpload, PoolUploadRow, setVisibleData } from 'discounts/redux'
import { AffectsCell, CodeStatus, UsageCount } from '../_shared'
import { ReactTableStateReducer } from 'uiComponents/table/reactTable/interface'
import { DateFormats } from 'utils'
import { setHash } from 'utils/hash'
import { KebabMenu } from 'uiComponents/menus/kebabMenu'
import { BaseRouteParams } from 'hocs'
import { LucideIconName } from 'utils/lucideIcon'
import { showErrorMessage } from 'uiComponents/message'

interface PoolUploadsTableProps extends ExtensibleReactTableComponentProps<DiscountPool> {
    children?: React.ReactNode
}

export const PoolUploadsTable: React.FC<PoolUploadsTableProps> = ({ row }) => {
    const routeParams = useParams<BaseRouteParams>()
    const location = useLocation()
    const dispatch = useDispatch()
    const poolId = row.original.uuid
    const childs = useMemo<PoolUploadRow[]>(() => {
        const codes = (row.original.codes || []).map((code) => ({
            id: code.id,
            name: code.code,
            status: 'valid',
            type: 'code' as const,
        }))
        const uploads = (row.original.uploads || []).map((upload) => ({
            id: upload.uuid,
            name: upload.name,
            status: upload.status,
            type: 'upload' as const,
        }))

        return [...codes, ...uploads].sort((childA, childB) => childA.name.localeCompare(childB.name))
    }, [])

    const downloadUpload = useCallback(
        (upload: PoolUploadRow) => {
            downloadFile(
                `${AppServices.accountService.backofficeEndpoint}dashboard_api/codes/export?account_slug=${routeParams.accountSlug}&import_id=${upload.id}&codes_type=discounts`,
                upload.name,
                { method: 'POST' },
                () => {
                    showErrorMessage('Failed to download', 'download_error')
                },
            )
        },
        [routeParams.accountSlug],
    )

    const columns = useMemo(() => {
        return [
            {
                accessor: 'name',
                Header: 'Name',
                className: 'name',
                Cell: ({ value }) => <span title={value}>{value}</span>,
            },
            {
                accessor: 'products',
                Header: 'Affects',
                className: 'affects',
                width: '6.25rem',
                disableSortBy: true,
                Cell: ({ row }) => {
                    const id = row.original.id
                    const { type, products } = useSelector(getDiscountUpload(id))
                    if (type === 'upload') {
                        return <>Any</>
                    }

                    return <AffectsCell productIds={products} />
                },
            },
            {
                accessor: 'uploadedBy',
                Header: 'Uploaded by',
                className: 'uploadedBy',
                width: '11rem',
                Cell: ({ row }) => {
                    const id = row.original.id
                    const { uploadedBy } = useSelector(getDiscountUpload(id))

                    return <span title={uploadedBy}>{uploadedBy}</span>
                },
            },
            {
                accessor: 'uploadedAt',
                Header: 'Upload date',
                className: 'uploadedAt',
                width: '10rem',
                Cell: ({ row }) => {
                    const id = row.original.id
                    const { uploadedAt } = useSelector(getDiscountUpload(id))

                    return (
                        <DateFormatter
                            value={uploadedAt}
                            format={`${DateFormats.LONG_DATE} ${DateFormats.SHORT_TIME}`}
                            fallback="-"
                        />
                    )
                },
            },
            {
                accessor: 'status',
                Header: 'Status',
                className: 'status',
                width: '13.5rem',
                headerProps: {
                    className: 'status',
                },
                Cell: ({ row }) => {
                    const id = row.original.id
                    const { status } = useSelector(getDiscountUpload(id))

                    return <CodeStatus status={status as any} />
                },
            },
            {
                accessor: 'usage',
                Header: 'Count',
                className: 'usage',
                width: '6.25rem',
                align: 'right',
                Cell: ({ row }) => {
                    const id = row.original.id
                    const { usage } = useSelector(getDiscountUpload(id))

                    return <UsageCount usage={usage} />
                },
            },
            {
                accessor: 'actions',
                className: 'actions',
                align: 'right',
                width: '4.5rem',
                style: { overflow: 'unset' },
                disableSortBy: true,
                Cell: ({ row }) => {
                    const id = row.original.id
                    const child = useSelector(getDiscountUpload(id))

                    const options = []
                    if (child.type === 'upload' && child.status) {
                        options.push({
                            label: 'Download CSV',
                            icon: 'ArrowDownToLine' as LucideIconName,
                            onClick: () => downloadUpload(child as PoolUploadRow),
                        })
                    }
                    if (child.type === 'code' && child.id) {
                        options.push({
                            label: 'Edit discount code',
                            icon: 'SquarePen' as LucideIconName,
                            onClick: () => {
                                setHash('edit_discount', { codeUuid: child.id!, poolUuid: poolId })
                            },
                        })
                    }
                    if (!!child.status && child.id) {
                        options.push({
                            label: 'Redeem unused codes',
                            icon: 'BadgeX' as LucideIconName,
                            onClick: () => {
                                setHash('redeem_unused_codes', { codeUuid: child.id!, codeType: child.type! })
                            },
                        })
                    }

                    return options.length ? <KebabMenu options={options} listAnchor="right" /> : null
                },
            },
        ] as TableColumn<PoolUploadRow>[]
    }, [routeParams, poolId, location.search, downloadUpload])

    const tableStateReducer = useCallback<ReactTableStateReducer<PoolUploadRow>>(
        (state, action, _, instance) => {
            const pageSize = instance?.state?.pageSize || 10
            const pageIndex = action?.pageIndex || instance?.state?.pageIndex
            const firstIndex = pageIndex ? pageIndex * pageSize : 0
            const ids = childs?.map((row) => row.id)?.slice(firstIndex, firstIndex + pageSize)

            if (ids) {
                dispatch(setVisibleData({ poolId, ids }))
            }

            return state
        },
        [poolId, childs],
    )

    return (
        <div className="pool-uploads-table-container">
            <ReactTable<PoolUploadRow>
                size="small"
                pagination="new"
                storeStateInQuery={false}
                stateReducer={tableStateReducer}
                rowProps={() => ({
                    className: 'pool-upload row',
                })}
                elevation={false}
                columns={columns}
                data={childs}
            />
        </div>
    )
}
